import { ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chip,
  Details,
  Heading,
  Paragraph,
  Table,
  Tag,
} from '@digdir/designsystemet-react';
import type {
  AreaMeasurement,
  WorkEnvironmentEvaluation,
} from '../../../api/workEnvironmentTypes';
import {
  CHANGE_FILTERS,
  ChangeFilter,
  filterAreas,
  comparedPeriods,
  readingKey,
} from '../evaluationView';
import { LEVEL_COLORS, oneDecimal } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './Evaluation.module.css';

type Props = {
  data: WorkEnvironmentEvaluation;
  areas: NonNullable<WorkEnvironmentEvaluation['result']>['areas'];
};

const CHANGE_COLORS = {
  better: 'success',
  worse: 'danger',
  same: 'neutral',
} as const;

export const ChangesCard = ({ data, areas }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const periods = comparedPeriods(
    data.baseline.createdAt,
    data.evaluation.createdAt,
    i18n.language,
  );
  const name = useId();
  const [filter, setFilter] = useState<ChangeFilter>('all');
  const shown = filterAreas(areas, filter);

  const measurement = (value: AreaMeasurement | null) =>
    value ? (
      <span className={classes.cell}>
        <Tag data-color={LEVEL_COLORS[value.level]} data-size="sm">
          {t(`workEnvironmentRisk.matrix.levels.${value.level}`)}
        </Tag>
        <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
          {[
            t('workEnvironmentEvaluation.result.changes.mean', {
              value: oneDecimal(value.score, i18n.language),
            }),
            t('workEnvironmentEvaluation.result.changes.likelihood', {
              value: value.likelihood,
            }),
            value.adjusted &&
              t('workEnvironmentEvaluation.result.changes.adjusted'),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    ) : (
      t('workEnvironmentEvaluation.result.changes.labels.none')
    );

  return (
    <section
      aria-labelledby="evaluation-changes-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="evaluation-changes-heading">
            {t('workEnvironmentEvaluation.result.changes.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentEvaluation.result.changes.intro')}
          </Paragraph>
        </div>
        <Details data-size="sm">
          <Details.Summary>
            {t('workEnvironmentEvaluation.result.changes.howTo.title')}
          </Details.Summary>
          <Details.Content>
            <div className={classes.howTo}>
              {(['mean', 'level', 'example'] as const).map((part) => (
                <Paragraph key={part} data-size="sm">
                  {t(`workEnvironmentEvaluation.result.changes.howTo.${part}`)}
                </Paragraph>
              ))}
            </div>
          </Details.Content>
        </Details>
        <div
          role="radiogroup"
          aria-label={t('workEnvironmentEvaluation.result.changes.filterLabel')}
          className={classes.filters}
        >
          {CHANGE_FILTERS.map((option) => (
            <Chip.Radio
              key={option}
              name={name}
              data-size="sm"
              checked={filter === option}
              onChange={() => setFilter(option)}
            >
              {t(`workEnvironmentEvaluation.result.changes.filter.${option}`, {
                count: filterAreas(areas, option).length,
              })}
            </Chip.Radio>
          ))}
        </div>
        {shown.length === 0 ? (
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentEvaluation.result.changes.empty')}
          </Paragraph>
        ) : (
          <div className={classes.tableScroll}>
            <Table data-size="sm">
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('workEnvironmentEvaluation.result.changes.area')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{periods.before}</Table.HeaderCell>
                  <Table.HeaderCell>{periods.after}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('workEnvironmentEvaluation.result.changes.change')}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {shown.map((area) => {
                  const reading = readingKey(area);
                  return (
                    <Table.Row key={area.key}>
                      <Table.Cell>
                        <span className={classes.stacked}>
                          <span>{area.name}</span>
                          <span
                            className={`ds-paragraph ${process.subtle}`}
                            data-size="xs"
                          >
                            {t(
                              `workEnvironmentEvaluation.result.changes.direction.${area.dimension}`,
                            )}
                          </span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>{measurement(area.before)}</Table.Cell>
                      <Table.Cell>{measurement(area.after)}</Table.Cell>
                      <Table.Cell>
                        {area.change ? (
                          <span className={classes.stacked}>
                            <Tag
                              data-color={CHANGE_COLORS[area.change]}
                              data-size="sm"
                            >
                              {t(
                                `workEnvironmentEvaluation.result.changes.labels.${area.change}`,
                              )}
                            </Tag>
                            {reading && (
                              <span
                                className={`ds-paragraph ${classes.reading}`}
                                data-size="xs"
                              >
                                {t(reading)}
                              </span>
                            )}
                          </span>
                        ) : (
                          t(
                            'workEnvironmentEvaluation.result.changes.labels.none',
                          )
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
    </section>
  );
};
